import { useRef } from 'react';
import {  motion, useScroll, useTransform } from "framer-motion";
import {  FiArrowUp } from "react-icons/fi";
import hero from './../../assets/vision/Accessibility.jpg';
import Header from '../../components/Navbar/Header';
import { WhyAccessibilityMatters } from './components/WhyAccessibilityMatters';
import { OurVow } from './components/OurVow';
import { HowWeMeasureSuccess } from './components/HowWeMesureSuccess';
import { AccessibilityCommitmentCard } from './components/AccessibilityCommitmentCard';
import { Card } from './components/ui/Card';
import { Button } from './components/ui/button';
import { CountUpStats } from './components/ui/CountUpStats';
import BackToVisionButton from './components/ui/BackToVisionButton';
import { ArrowRight } from 'lucide-react';

const IconWrapper = ({ children }) => (
  <div className="rounded-full bg-gray-100 p-3 text-gray-600">
    {children}
  </div>
);

const ScreenReaderIcon = () => (
  <IconWrapper>
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  </IconWrapper>
);

const KeyboardIcon = () => (
  <IconWrapper>
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
    </svg>
  </IconWrapper>
);

const ContrastIcon = () => (
  <IconWrapper>
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  </IconWrapper>
);

const FeatureCard = ({ icon: Icon, title, description }) => (
  <Card>
    <Icon />
    <h3 className="text-xl font-semibold mt-4 mb-2">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </Card>
);

const Section = ({ title, children, className = "" }) => (
  <section className={`py-16 ${className} px-2 mx-2`}>
    <h2 className="text-3xl font-bold text-center mb-8">{title}</h2>
    {children}
  </section>
);

const commitmentItems = [
  {
    id: 1,
    title: "Inclusive Design",
    description: "Building interfaces that prioritize simplicity, clarity, and usability for all. We ensure full compatibility with assistive technologies like screen readers, voice navigation, and text-to-speech tools.",
    img: "/vision/access/acc(1).jpeg",
  },
  {
    id: 2,
    title: "Language and Localization",
    description: "Offering multi-language support for global inclusivity. We adapt content to reflect cultural sensitivities and diverse linguistic needs, ensuring our platform is accessible to users worldwide.",
    img: "/vision/access/acc(2).jpeg",
  },
  {
    id: 3,
    title: "Economic Accessibility",
    description: "Providing affordable solutions with transparent pricing to eliminate economic barriers. We're expanding services to underserved communities, empowering users who have been overlooked by traditional systems.",
    img: "/vision/access/acc(3).jpeg",
  },
  {
    id: 4,
    title: "Digital Transparency",
    description: "Ensuring all features are easy to find, understand, and use, with robust FAQs and accessible customer support. We offer visual, auditory, and text-based instructions for diverse learning styles.",
    img: "/vision/access/acc(4).jpeg",
  },
  {
    id: 5,
    title: "Continuous Feedback",
    description: "Inviting users to share accessibility challenges and solutions through dedicated feedback loops. We partner with advocacy groups to stay ahead of emerging needs and continuously improve our platform.",
    img: "/vision/access/acc(5).jpeg",
  },
];

const OppoScroll = () => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
  });

  return (
    <>
     
      <section ref={targetRef} className="flex bg-black text-white">
        <Content content={commitmentItems} />
        <Images content={commitmentItems} scrollYProgress={scrollYProgress} />
      </section>
 
    </>
  );
};

const Content = ({ content }) => {
  return (
    <div className="w-full md:w-1/2">
      {content.map(({ id, title, description }, idx) => (
        <div
          key={id}
          className={`p-8 h-screen flex flex-col justify-center ${
            idx % 2 ? "bg-white text-black" : "bg-black text-white"
          }`}
        >
          <h3 className="text-3xl font-medium mb-4">{title}</h3>
          <p className="font-light w-full max-w-md">{description}</p>
        </div>
      ))}
    </div>
  );
};

const Images = ({ content, scrollYProgress }) => {
  const top = useTransform(
    scrollYProgress,
    [0, 1],
    [`-${(content.length - 1) * 100}vh`, "0vh"]
  );

  return (
    <div className="h-screen overflow-hidden sticky top-0 w-24 md:w-1/2 hidden md:block">
      <motion.div style={{ top }} className="absolute left-0 right-0">
        {[...content].reverse().map(({ img, id, title }) => (
          <img
            key={id}
            alt={title}
            className="h-screen w-full object-cover"
            src={img}
          />
        ))}
      </motion.div>
    </div>
  );
};

export default function Accessibility() {
  return (
    <div className="min-h-screen bg-white text-black">
      <Header
        className="fixed top-0 w-full z-10"
        initialTextColor="black"
        initialIconColor="black"
        forceIconColor="black"
        disableHoverEffect={true}
      />
      <main>
        {/* Hero Section */}
        <section
          className="pt-32 pb-16 bg-cover bg-center text-white"
          style={{
            backgroundImage: `url(${hero})`,
          }}
        >
           <div className="fixed top-20 left-4 z-40">
        <BackToVisionButton />
      </div>
          <div className="text-center px-4">
            <h1 className="text-5xl font-bold mb-6 mt-40">
              Empowering Entrepreneurs Through Accessibility
            </h1>
            <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
              At FuteurAI, accessibility is our foundation. We're creating a platform where every entrepreneur, regardless of ability, can thrive in the modern business landscape.
            </p>
          
          </div>
        </section>
        <Section title="Breaking Barriers, Unlocking Potential">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-gray-600">
              Globally, more than 15% of the population lives with a disability. At FuteurAI, we believe innovation must include everyone, ensuring every entrepreneur has an equal opportunity to succeed.
            </p>
          </div>
          <CountUpStats />
        </Section>

        <WhyAccessibilityMatters />
        <Section title="Our Commitment">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-gray-600">
              At FuteurAI, we understand that accessibility isn't just a feature—it's a responsibility. We're committed to building an inclusive platform that empowers all users, regardless of their abilities, background, or circumstances.
            </p>
          </div>
          <OppoScroll />
        </Section>
        <OurVow />
        <Section title="Our Accessibility Commitment">
          <AccessibilityCommitmentCard
            headline="Redefining Accessibility in Fintech: A Commitment to Empowerment for All"
            subheadline="In a digital-first world, accessibility isn't optional—it's essential. FuteurAI vows to break barriers in fintech, ensuring everyone, everywhere, has the tools they need to thrive."
            content={[
              {
                title: "Why Accessibility Matters in Fintech",
                items: [
                  "Complex Interfaces: Tools that are difficult to navigate for users with disabilities or limited digital literacy.",
                  "Lack of Inclusivity: Systems designed without considering diverse needs such as language barriers or adaptive technologies.",
                  "Digital Barriers: Limited compatibility with screen readers, voice recognition software, and other assistive devices.",
                  "Economic Disparities: High costs of entry, excluding underbanked and underserved communities.",
                ],
              },
              {
                title: "Our Commitment: Accessibility as a Core Value",
                items: [
                  "Inclusive Design: Building interfaces that prioritize simplicity, clarity, and usability for all.",
                  "Language and Localization: Offering multi-language support for global inclusivity.",
                  "Economic Accessibility: Providing affordable solutions with transparent pricing to eliminate economic barriers.",
                  "Digital Transparency: Ensuring all features are easy to find, understand, and use.",
                  "Continuous Feedback: Inviting users to share accessibility challenges and solutions.",
                ],
              },
            ]}
          
          />
        </Section>
        <HowWeMeasureSuccess />

        <Section title="Building Tools for Tomorrow's Entrepreneurs">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-gray-600">
              Our commitment doesn't stop here. FuteurAI is constantly innovating to ensure accessibility evolves with technology, empowering entrepreneurs for decades to come.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <FeatureCard 
              icon={ScreenReaderIcon}
              title="Voice Command Integration"
              description="Control your dashboard with just your voice."
            />
            <FeatureCard 
              icon={KeyboardIcon}
              title="AI-Powered Personalization"
              description="Adaptive interfaces tailored to individual user needs."
            />
            <FeatureCard 
              icon={ContrastIcon}
              title="Haptic Feedback Technology"
              description="Enhanced tactile interactions for visually impaired users."
            />
          </div>
        </Section>

        <Section title="Exceeding Industry Benchmarks" className="bg-gray-50">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-gray-600">
              We align with the highest accessibility standards, including WCAG 2.1 AA and Section 508 compliance. But we're not stopping there—we aim to set new benchmarks in inclusive fintech.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <FeatureCard 
              icon={ScreenReaderIcon}
              title="Regular Audits"
              description="Proactive checks to maintain and improve accessibility standards."
            />
            <FeatureCard 
              icon={KeyboardIcon}
              title="Community Feedback"
              description="Incorporating real-world insights from users to shape our tools and services."
            />
          </div>
        </Section>

        <Section title="Join Us in Redefining Accessibility" className="text-center">
          <div className="max-w-3xl mx-auto mb-8">
            <p className="text-gray-600">
              We believe accessibility is about more than technology—it's about creating a world where everyone has a seat at the table. Join us in redefining what's possible.
            </p>
          </div>
          <Button 
          size="lg" 
          variant="default" 
          className="group" 
          onClick={() => {
            window.location.href = 'https://app.futeur.ai/signup';
            setTimeout(() => window.scrollTo(0, 0), 0); // Ensure the scroll happens after navigation
          }}
        >
        Get Started With FuteurAI
          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Button>
        </Section>
      </main>
    </div>
  )
}

